import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { buildProviders, Coerce } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, pairwise, startWith, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { CustomFormElement } from 'src/app/_core/tools/custom-form-element';
import { ActivitySalesState } from 'src/app/board/activity/stores/activity-sales/activity-sales.state';
import { arrayEmpty, arrayNotEmpty } from 'src/app/utils/array.utils';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import { ComboBoxStatutsModel, MutationGetListeStatutAdmForFiltreArgs } from 'src/graphql/generated-types';

export interface SharedAdministrativeStatus {
    administrativeStatus: { id: number, code: string }[];
    complementaryStatus: number[];
}

@Component({
    selector: 'app-shared-administrative-status',
    templateUrl: './shared-administrative-status.component.html',
    styleUrls: ['./shared-administrative-status.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedAdministrativeStatusComponent),
    standalone: false
})
export class SharedAdministrativeStatusComponent extends CustomFormElement<SharedAdministrativeStatus> implements OnInit, OnDestroy {

    @Coerce('boolean')
    @Input()
    isSupplier = false;

    @Coerce('boolean')
    @Input()
    displayChildStatus = true;

    @Coerce('boolean')
    @Input()
    displayParentsStatus = true;

    @Coerce('boolean')
    @Input()
    multiple = true;

    @Input()
    placeholderValue: string = null;

    @Input()
    displayedAdministrativeStatus: string;

    administrativeStatusList: ComboBoxStatutsModel[] = [];

    formControl = new UntypedFormControl();

    loading = false;

    filteredAdministrativeStatusList: ComboBoxStatutsModel[] = null;

    private subscriptionsList: Subscription[] = [];

    constructor(private readonly graphqlService: GraphqlService,
                private readonly store: Store,
                private readonly cdr: ChangeDetectorRef) {
        super();
    }

    private _fitlerByParentsStatusList = new BehaviorSubject(null);

    get fitlerByParentsStatusList(): string[] {
        return this._fitlerByParentsStatusList.getValue();
    }

    @Input()
    set fitlerByParentsStatusList(list: string[]) {
        this._fitlerByParentsStatusList.next(list);
    }

    get fitlerByParentsStatusList$(): Observable<string[]> {
        return this._fitlerByParentsStatusList.asObservable();
    }

    private _administrativeStatus = new BehaviorSubject<SharedAdministrativeStatus>(null);

    get administrativeStatus(): SharedAdministrativeStatus {
        return this._administrativeStatus.getValue();
    }

    get administrativeStatus$(): Observable<SharedAdministrativeStatus> {
        return this._administrativeStatus.asObservable();
    }

    writeValue(value: SharedAdministrativeStatus): void {
        this._administrativeStatus.next(value);
        super.writeValue(value);
    }

    private getAdministrativeStatusList$(): Observable<any> {
        return this.graphqlService.mutateOne<'GetListeStatutAdmForFiltre', MutationGetListeStatutAdmForFiltreArgs>({
            mutation: gql`
                mutation GetListeStatutAdmForFiltre($contexte: ContextInfosInput, $criteres: CriteresRechercheStatutsModelInput) {
                    GetListeStatutAdmForFiltre(
                        contexte: $contexte,
                        criteres: $criteres
                    ) {
                        ComboBoxStatutsModel {
                            IdStatut
                            CodeStatut
                            CodeStatutPere
                            LibelleStatut
                            IsStatutPerso
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    IdActivite: this.store.selectSnapshot(ActivitySalesState.currentActivityId),
                    CodeStatutPere: this.displayedAdministrativeStatus,
                    DisplayStatutsPeres: this.displayParentsStatus,
                    DisplayStatutsFils: this.displayChildStatus,
                    IsContexteFournisseur: this.isSupplier,
                    IsClos: false
                }
            }
        }).pipe(
            tap(GetListeStatutAdmForFiltre => {
                this.administrativeStatusList = GetListeStatutAdmForFiltre?.ComboBoxStatutsModel || [];
                this.cdr.markForCheck();
            })
        );
    }

    private normalize(): SharedAdministrativeStatus {
        const statusByProperty = (
            property: keyof ComboBoxStatutsModel,
            {IsStatutPerso} = {IsStatutPerso: false}
        ) => this.administrativeStatusList
            .filter(administrativeStatus => administrativeStatus.IsStatutPerso === IsStatutPerso)
            .filter(status => this.formControl.value?.includes(status[property]));

        return {
            administrativeStatus: statusByProperty('CodeStatut').map(status => {
                return {
                    id: status.IdStatut,
                    code: status.CodeStatut
                };
            }),
            complementaryStatus: statusByProperty('IdStatut', {IsStatutPerso: true}).map(status => status.IdStatut)
        };
    }

    private unsubscribeSuscriptions(): void {
        this.subscriptionsList.forEach(s => s.unsubscribe());
    }

    private initSubscription(): void {
        this.subscriptionsList.push(
            this.formControl.valueChanges.pipe(
                this.untilDestroyed,
                startWith(this.formControl.value),
                filter((newlySelectedStatusCode) => {
                    if (!this.multiple) {
                        const value = [...Array.from([newlySelectedStatusCode])];
                        if (arrayNotEmpty(value)) {
                            this.formControl.patchValue(value, {emitEvent: false});
                        }
                        this.notifyUpdate(this.normalize());
                    }
                    return this.multiple;
                }),
                pairwise(),
                tap(([selectedStatusCode, newlySelectedStatusCode]) => {
                    const diff = newlySelectedStatusCode?.filter(codes => !selectedStatusCode?.includes(codes));

                    if (!diff) {
                        return;
                    }

                    const complementaryStatusList = this.administrativeStatusList.filter(status => status.CodeStatutPere === diff?.[0]);
                    const value = [...newlySelectedStatusCode, ...complementaryStatusList.map(({IdStatut}) => IdStatut)];
                    if (arrayNotEmpty(value)) {
                        this.formControl.patchValue(value, {emitEvent: false});
                    }
                }),
                tap(() => {
                    this.notifyUpdate(this.normalize());
                })
            ).subscribe()
        );

        this.subscriptionsList.push(
            this.fitlerByParentsStatusList$.pipe(
                tap(list => {
                    if (!list) {
                        this.filteredAdministrativeStatusList = this.administrativeStatusList;
                        return;
                    }
                    this.filteredAdministrativeStatusList = this.administrativeStatusList.filter(status =>
                        !status.IsStatutPerso || list.includes(status.CodeStatutPere)
                    );
                    this.formControl[arrayEmpty(this.filteredAdministrativeStatusList) ? 'disable' : 'enable']();
                    this.formControl.patchValue(null, {emitEvent: false});
                    this.cdr.markForCheck();
                })
            ).subscribe());
    }

    ngOnInit(): void {
        this.loading = true;

        this.watch$ = this.getAdministrativeStatusList$().pipe(
            tap(() => {
                this.watch$ = this.administrativeStatus$.pipe(
                    tap(value => {
                        this.unsubscribeSuscriptions();

                        if (arrayEmpty(value)) {
                            this.formControl.patchValue([]);
                        } else {
                            this.formControl.patchValue([
                                ...(value.administrativeStatus?.map(({code}) => code) || []),
                                ...(value.complementaryStatus?.map(id => id) || [])
                            ]);
                        }
                        this.initSubscription();
                    })
                );
            }),
            wheneverEnd(() => this.loading = false)
        );
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.unsubscribeSuscriptions();
    }
}
